import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  inject,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import type {
  TableColumn,
  PaginationEvent,
} from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { VirtualWarehouseService } from '../services/virtual-warehouse.service';
import type { VirtualWarehouse } from '../models/virtual-warehouse.model';
import {
  VietnamAddressService,
  VietnamProvince,
} from '../../services/vietnam-address.service';
import {
  VIRTUAL_WAREHOUSE_COLUMNS,
  VIRTUAL_WAREHOUSE_DEFAULT_COLUMNS_ORDER,
  VIRTUAL_WAREHOUSE_FLEXIBLE_COLUMNS_ORDER,
  VIRTUAL_WAREHOUSE_ACTION_COLUMN,
  VIRTUAL_WAREHOUSE_PAGE_SEARCH_CONFIG,
} from '../virtual-warehouse.config';
import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { StatusFilterComponent } from '@shared/components/status-filter/status-filter.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import {
  buildWarehouseDefaultColumns,
  buildWarehouseFlexibleColumns,
} from '../../utils/warehouse-table-columns.util';
import { buildWarehouseStatusChangeDialogState } from '../../utils/warehouse-confirmation-dialog.util';
import {
  WAREHOUSE_LIST_STATUS_TAG_STYLE,
  warehouseListStatusDisplayText,
  warehouseListStatusSeverity,
} from '../../utils/warehouse-status-tag.util';
import { translateWarehouseProvinceCityName } from '../../utils/warehouse-province-display.util';
import { formatMemberWarehouseLabels } from '../../utils/warehouse-member-format.util';
import { openWarehouseTableColumnSelector } from '../../utils/warehouse-column-selector.util';
import {
  mergeVirtualWarehousePagePagination,
  normalizeVirtualWarehousePageForm,
  resolveCityNameForVirtualPageApi,
  virtualWarehousePageHasActiveFilters,
  virtualWarehousePageResetFields,
} from '../../utils/warehouse-virtual-page-search.util';
import { PermissionService } from '@app/core/services/permission.service';
import { AuthService } from '@app/core/services/auth.service';
import { switchMap } from 'rxjs';

/** Virtual warehouse type codes for filter (API values). */
const VIRTUAL_WAREHOUSE_TYPE_CODES = [
  'DEFAULT_VIRTUAL',
  'QTY_VIRTUAL',
  'RATIO_VIRTUAL',
  'INTERLINE_VIRTUAL',
] as const;

export interface VirtualWarehousePageSearchParams {
  name?: string;
  code?: string[];
  typeCodes?: string[];
  cityCode?: number;
  cityName?: string;
  page: number;
  size: number;
}

@Component({
  selector: 'app-virtual-warehouse-page-list',
  standalone: true,
  imports: [
    CommonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    TranslateModule,
    SearchFormComponent,
    AppButtonComponent,
    StatusFilterComponent,
    AdvancedDataTableComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './virtual-warehouse-page-list.component.html',
  styleUrl: './virtual-warehouse-page-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualWarehousePageListComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly virtualWarehouseService = inject(VirtualWarehouseService);
  private readonly translateService = inject(TranslateService);
  private readonly vietnamAddressService = inject(VietnamAddressService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly authService = inject(AuthService);
  private readonly destroy$ = new Subject<void>();

  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.virtual_warehouse.create');
  });
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.virtual_warehouse.view');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.virtual_warehouse.edit');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.virtual_warehouse.approve');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.virtual_warehouse.delete');
  });

  showConfirmationDialog = false;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'statusChange' | null = null;
  selectedWarehouseForAction: VirtualWarehouse | null = null;

  provinces = signal<VietnamProvince[]>([]);
  private currentLang = signal<string>('vi');

  searchParams = signal<VirtualWarehousePageSearchParams>({
    page: 0,
    size: 10,
  });

  primaryButton = computed(() => ({
    label: this.translateService.instant('common.actions.new'),
    severity: 'info' as const,
    outlined: false,
    show: this.canCreate(),
    icon: 'add',
  }));

  searchConfig = computed(() => {
    const lang = this.currentLang();
    const provinces = this.provinces();
    const base = [...VIRTUAL_WAREHOUSE_PAGE_SEARCH_CONFIG];
    const typeField = base.find((f) => f.key === 'typeCodes');
    if (typeField) {
      const typeI18nKeyMap: Record<string, string> = {
        DEFAULT_VIRTUAL: 'default_virtual',
        QTY_VIRTUAL: 'qty_virtual',
        RATIO_VIRTUAL: 'ratio_virtual',
        INTERLINE_VIRTUAL: 'interline_virtual',
      };
      typeField.options = VIRTUAL_WAREHOUSE_TYPE_CODES.map((code) => ({
        label: this.translateService.instant(
          `virtual_warehouse.warehouse.types.${typeI18nKeyMap[code] ?? code}`,
        ),
        value: code,
      }));
    }
    return withPaginatedSearchFields(base, {
      cityCode: {
        loadPage: (params) =>
          this.vietnamAddressService.getProvinceOptionsPage(params, lang),
      },
    });
  });

  private readonly hasAnyActionPermission = computed(
    () => this.canUpdate() || this.canApprove() || this.canDelete(),
  );

  defaultColumns = computed((): TableColumn[] =>
    buildWarehouseDefaultColumns(
      VIRTUAL_WAREHOUSE_COLUMNS,
      VIRTUAL_WAREHOUSE_DEFAULT_COLUMNS_ORDER,
      VIRTUAL_WAREHOUSE_ACTION_COLUMN.ACTIONS,
      this.hasAnyActionPermission(),
    ),
  );

  flexibleColumns = computed((): TableColumn[] =>
    buildWarehouseFlexibleColumns(
      VIRTUAL_WAREHOUSE_COLUMNS,
      VIRTUAL_WAREHOUSE_FLEXIBLE_COLUMNS_ORDER,
    ),
  );

  warehouses = signal<VirtualWarehouse[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  page = computed(() => this.searchParams().page);
  size = computed(() => this.searchParams().size);

  warehousesData = computed(() => this.warehouses());

  ngOnInit(): void {
    this.currentLang.set(
      this.translateService.defaultLang ||
        this.translateService.getDefaultLang() ||
        'vi',
    );
    this.vietnamAddressService
      .loadProvinces()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (p) => this.provinces.set(p),
        error: () => {},
      });
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((e) => this.currentLang.set(e.lang || 'vi'));
    this.loadWarehouses();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadWarehouses(): void {
    const params = this.searchParams();
    this.loading.set(true);
    const cityCode = params.cityCode;
    const cityName = resolveCityNameForVirtualPageApi(
      cityCode,
      this.provinces(),
      this.currentLang(),
    );
    this.virtualWarehouseService
      .getVirtualWarehousesFromVirtualPage({
        page: params.page,
        size: params.size,
        name: params.name?.trim() || undefined,
        code: params.code?.length ? params.code : undefined,
        typeCodes: params.typeCodes?.length ? params.typeCodes : undefined,
        cityCode: cityCode ?? undefined,
        cityName,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.warehouses.set(res.content || []);
          this.totalRecords.set(res.totalElements || 0);
          this.loading.set(false);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'virtual_warehouse.messages.error.title',
            ),
            detail: this.translateService.instant(
              'virtual_warehouse.messages.error.failed_to_load',
            ),
          });
          this.loading.set(false);
        },
      });
  }

  onSearch(formValue: Record<string, unknown>): void {
    this.searchParams.set(
      normalizeVirtualWarehousePageForm(
        formValue,
        this.searchParams().size,
      ) as VirtualWarehousePageSearchParams,
    );
    this.loadWarehouses();
  }

  onReset(): void {
    const current = this.searchParams();
    if (!virtualWarehousePageHasActiveFilters(current)) {
      return;
    }
    this.searchParams.set({
      ...virtualWarehousePageResetFields(current.size),
    });
    this.loadWarehouses();
  }

  onNew(): void {
    if (!this.canCreate()) {
      return;
    }
    this.router.navigate(['/warehouse/virtual-page/add']);
  }

  onPageChange(event: PaginationEvent): void {
    this.searchParams.update((p) => ({
      ...p,
      ...mergeVirtualWarehousePagePagination(
        { page: p.page, size: p.size },
        event,
      ),
    }));
    this.loadWarehouses();
  }

  getTranslatedCityName(cityName: string | undefined): string {
    return translateWarehouseProvinceCityName(
      cityName,
      this.provinces(),
      this.currentLang(),
    );
  }

  formatMemberWarehouses(memberWarehouseNames: string[] | undefined): string {
    return formatMemberWarehouseLabels(memberWarehouseNames);
  }

  private readonly typeI18nKeyMap: Record<string, string> = {
    DEFAULT_VIRTUAL: 'default_virtual',
    QTY_VIRTUAL: 'qty_virtual',
    RATIO_VIRTUAL: 'ratio_virtual',
    INTERLINE_VIRTUAL: 'interline_virtual',
  };

  getTypeDisplayText(typeCode: string | undefined): string {
    if (!typeCode?.trim()) return '-';
    const key =
      this.typeI18nKeyMap[typeCode] ??
      typeCode.replace(/-/g, '_').toLowerCase();
    const i18nKey = `virtual_warehouse.warehouse.types.${key}`;
    const translated = this.translateService.instant(i18nKey);
    return translated !== i18nKey ? translated : typeCode;
  }

  onEdit(warehouse: VirtualWarehouse): void {
    if (!this.canUpdate()) return;
    this.router.navigate(['/warehouse/virtual-page/edit', warehouse.id]);
  }

  onStatusChange(warehouse: VirtualWarehouse): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    const newStatus = !warehouse.status;
    this.selectedWarehouseForAction = warehouse;
    const dlg = buildWarehouseStatusChangeDialogState(
      (k, p) => this.translateService.instant(k, p),
      'virtual_warehouse',
      warehouse.name,
      newStatus,
      true,
    );
    this.dialogTitle = dlg.title;
    this.dialogMessage = dlg.message;
    this.dialogButtons = dlg.buttons;
    this.currentAction = 'statusChange';
    this.showConfirmationDialog = true;
  }

  onConfirmatiButtonClick(action: string): void {
    if (action === 'cancel') {
      this.closeConfirmationDialog();
    } else if (action === 'confirm' && this.currentAction === 'statusChange') {
      this.confirmStatusChange();
    }
  }

  confirmStatusChange(): void {
    if (this.selectedWarehouseForAction) {
      this.changeVirtualWarehouseStatus();
    }
    this.closeConfirmationDialog();
  }

  closeConfirmationDialog(): void {
    this.showConfirmationDialog = false;
    this.selectedWarehouseForAction = null;
    this.currentAction = null;
    this.dialogTitle = '';
    this.dialogMessage = '';
    this.dialogButtons = [];
  }

  private changeVirtualWarehouseStatus(): void {
    const warehouse = this.selectedWarehouseForAction;
    if (!warehouse?.id) return;
    const newStatus = !warehouse.status;
    const updatedBy = this.authService.currentUser()?.username ?? 'admin';
    this.virtualWarehouseService
      .getVirtualWarehouseDetailApi(warehouse.id)
      .pipe(
        switchMap((detail) => {
          const memberIds = (detail.members ?? [])
            .map((m) => m.id)
            .filter((id): id is number => id != null);
          return this.virtualWarehouseService.updateVirtualWarehouseApi(
            warehouse.id!,
            {
              name: detail.name ?? warehouse.name ?? '',
              status: newStatus,
              memberWarehouseIds: memberIds,
              updatedBy,
            },
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant(
              'virtual_warehouse.messages.success.title',
            ),
            detail: this.translateService.instant(
              `virtual_warehouse.messages.success.warehouse_${
                newStatus ? 'activated' : 'deactivated'
              }`,
            ),
            life: 5000,
          });
          this.loadWarehouses();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'virtual_warehouse.messages.error.title',
            ),
            detail: this.translateService.instant(
              'virtual_warehouse.messages.error.failed_to_update_status',
            ),
            life: 6000,
          });
        },
      });
  }

  onDelete(warehouse: VirtualWarehouse): void {
    if (!this.canDelete() || warehouse.status) return;
    this.router.navigate(['/warehouse/virtual-page/edit', warehouse.id]);
  }

  getStatusDisplayText(status: boolean): string {
    return warehouseListStatusDisplayText(
      (k) => this.translateService.instant(k),
      'virtual_warehouse',
      status,
    );
  }

  getStatusSeverity(status: boolean): string {
    return warehouseListStatusSeverity(status);
  }

  getStatusStyle(_status: boolean): Record<string, string> {
    return { ...WAREHOUSE_LIST_STATUS_TAG_STYLE };
  }

  formatDateTime = formatDateTime;

  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    openWarehouseTableColumnSelector(this.advancedTable, event);
  }
}
